import React from "react";

import { faCheck, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { commitLocalUpdate, commitMutation, graphql, type RecordSourceSelectorProxy } from "relay-runtime";

import { environment } from "~/models";
import { Button } from "~/ui";

import { type AcceptDismissPopoverAcceptMutation } from "~/__generated__/AcceptDismissPopoverAcceptMutation.graphql";
import type { AcceptDismissPopoverDismissMutation } from "~/__generated__/AcceptDismissPopoverDismissMutation.graphql";

import styles from "./AcceptDismissPopover.module.css";

const acceptActionItemMutation = graphql`
	mutation AcceptDismissPopoverAcceptMutation($actionItemIds: [ID!]!) {
		acceptAiActionItems(actionItemIds: $actionItemIds) {
			ok
		}
	}
`;

const dismissActionItemMutation = graphql`
	mutation AcceptDismissPopoverDismissMutation($actionItemIds: [ID!]!) {
		dismissAiActionItems(actionItemIds: $actionItemIds) {
			ok
		}
	}
`;

export default function AcceptDismissPopover({
	actionItemId,
	floatingRef,
	floatingStyles,
}: {
	actionItemId: string;
	floatingRef: (node: HTMLElement | null) => void;
	floatingStyles: React.CSSProperties;
}) {
	const updateRelayStoreForAcceptance = React.useCallback(
		(store: RecordSourceSelectorProxy<object>) => {
			const node = store.get(actionItemId);
			node?.setValue(false, "aiGenerated");
		},
		[actionItemId],
	);

	const updateRelayStoreForDeletion = React.useCallback(
		(store: RecordSourceSelectorProxy<object>) => {
			store.delete(actionItemId);
		},
		[actionItemId],
	);

	const accept = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			commitMutation<AcceptDismissPopoverAcceptMutation>(environment, {
				mutation: acceptActionItemMutation,
				variables: { actionItemIds: [actionItemId] },

				// We want to update the aiGenerated value in the relay store in the optimisticUpdater so that the UI
				// reflects the change right away. We also need to perform this update once the mutation has completed
				// because the mutation response will override the optimistic update. Since the mutation kicks off an
				// async celery task to perform the db operations, this will override the value to aiGenerated = true.
				onCompleted: () => {
					commitLocalUpdate(environment, store => updateRelayStoreForAcceptance(store));
				},
				optimisticUpdater: store => updateRelayStoreForAcceptance(store),
			});
		},
		[actionItemId, updateRelayStoreForAcceptance],
	);

	const dismiss = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			commitMutation<AcceptDismissPopoverDismissMutation>(environment, {
				mutation: dismissActionItemMutation,
				variables: { actionItemIds: [actionItemId] },
				// See comment in accept() for why we're modifying store records twice
				onCompleted: () => {
					commitLocalUpdate(environment, store => updateRelayStoreForDeletion(store));
				},
				optimisticUpdater: store => updateRelayStoreForDeletion(store),
			});
		},
		[actionItemId, updateRelayStoreForDeletion],
	);

	return (
		<div ref={floatingRef} className={styles.popover} style={floatingStyles} key={actionItemId}>
			<Button className={styles.acceptDismissButton} onClick={dismiss}>
				<FontAwesomeIcon icon={faTrashCan} />
				Dismiss
			</Button>
			<Button className={styles.acceptDismissButton} onClick={accept}>
				<FontAwesomeIcon icon={faCheck} />
				Accept
			</Button>
		</div>
	);
}
